import { useEffect, useRef, useState } from "react";
import { es } from "date-fns/locale";
import { CalendarDays, ChevronUp } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";

const p2 = (n) => String(n).padStart(2, "0");
const toDate = (s) => { if (!s) return undefined; const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d); };
const toStr = (d) => `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`;
const show = (s) => (s ? s.split("-").reverse().join("-") : "");

// Inline calendar by default (works inside iPhone dialogs); `floating` overlays it on page layouts.
const YEAR_NAV = {
  caption_label: "hidden", caption_dropdowns: "flex gap-1", vhidden: "sr-only",
  dropdown: "rounded-md border border-input bg-background px-1 py-0.5 text-sm", dropdown_month: "", dropdown_year: "",
};

export function DateField({ value, onChange, min, max, testId, placeholder = "dd-mm-aaaa", floating = false, yearNav = false }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const minD = toDate(min), maxD = toDate(max);
  const disabled = [minD && { before: minD }, maxD && { after: maxD }].filter(Boolean);
  useEffect(() => {
    if (!floating || !open) return undefined;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("pointerdown", close);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("pointerdown", close); document.removeEventListener("keydown", esc); };
  }, [floating, open]);
  return (
    <div className={cn("w-full", floating && "relative")} ref={ref}>
      <button type="button" data-testid={testId} onClick={() => setOpen((o) => !o)} aria-expanded={open}
        className={cn("flex h-9 w-full items-center justify-between gap-2 rounded-md border border-input bg-transparent px-3 text-sm shadow-sm transition-colors hover:border-primary/60 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring touch-manipulation",
          !value && "text-muted-foreground", open && "border-primary")}>
        <span className="whitespace-nowrap truncate">{value ? show(value) : placeholder}</span>
        {open ? <ChevronUp className="w-4 h-4 text-primary shrink-0" /> : <CalendarDays className="w-4 h-4 text-primary shrink-0" />}
      </button>
      {open && (
        <div className={cn("rounded-xl border bg-popover shadow-md w-fit",
          floating ? "max-w-[calc(100vw-2rem)]" : "max-w-full overflow-x-auto",
          floating ? "absolute left-0 top-full z-50 mt-1 shadow-xl animate-in fade-in-0 zoom-in-95" : "mt-2")} data-testid={`${testId}-calendar`}>
          <Calendar mode="single" locale={es} weekStartsOn={1} selected={toDate(value)}
            defaultMonth={toDate(value) || (yearNav ? maxD : minD)} fromDate={minD} toDate={maxD} disabled={disabled}
            {...(yearNav ? { captionLayout: "dropdown-buttons", fromYear: (minD || new Date(1930, 0, 1)).getFullYear(), toYear: (maxD || new Date()).getFullYear(), classNames: YEAR_NAV } : {})}
            onSelect={(d) => { if (d) { onChange(toStr(d)); setOpen(false); } }} />
        </div>
      )}
    </div>
  );
}
